import { Container, Graphics, Text, type FederatedPointerEvent } from 'pixi.js';
import { Button, type ButtonVariant } from './Button';
import { Panel } from './Panel';
import { UI_COLORS, UI_TEXT_STYLES } from './Theme';

export interface ModalAction {
  label: string;
  variant?: ButtonVariant;
  disabled?: boolean;
  onPress?: () => void;
}

export interface ModalOptions {
  width?: number;
  height?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  title?: string;
  message?: string;
  actions?: ModalAction[];
  onDismiss?: () => void;
}

export class Modal extends Container {
  private readonly overlay = new Graphics();
  private readonly panel: Panel;
  private readonly messageText = new Text({
    text: '',
    style: {
      ...UI_TEXT_STYLES.modalBody,
      wordWrapWidth: 560
    }
  });
  private readonly actionLayer = new Container();
  private readonly actionButtons: Button[] = [];

  private modalWidth: number;
  private modalHeight: number;
  private viewportWidth: number;
  private viewportHeight: number;
  private onDismiss?: () => void;

  public constructor(options: ModalOptions = {}) {
    super();
    this.modalWidth = options.width ?? 680;
    this.modalHeight = options.height ?? 360;
    this.viewportWidth = options.viewportWidth ?? 1280;
    this.viewportHeight = options.viewportHeight ?? 720;
    this.onDismiss = options.onDismiss;
    this.panel = new Panel({
      width: this.modalWidth,
      height: this.modalHeight,
      title: options.title ?? '確認'
    });

    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'default';
    this.overlay.on('pointertap', this.handleOverlayTap);

    this.addChild(this.overlay);
    this.addChild(this.panel);
    this.panel.content.addChild(this.messageText);
    this.panel.content.addChild(this.actionLayer);

    this.setMessage(options.message ?? '');
    this.setActions(options.actions ?? []);
    this.draw();
  }

  public setTitle(title: string): void {
    this.panel.setTitle(title);
  }

  public setMessage(message: string): void {
    this.messageText.text = message;
    this.layout();
  }

  public setActions(actions: ModalAction[]): void {
    for (const button of this.actionButtons.splice(0)) {
      button.destroy({ children: true });
    }
    this.actionLayer.removeChildren();

    for (const action of actions) {
      const button = new Button({
        label: action.label,
        width: 190,
        height: 52,
        variant: action.variant ?? 'secondary',
        disabled: action.disabled,
        onPress: action.onPress
      });
      this.actionButtons.push(button);
      this.actionLayer.addChild(button);
    }

    this.layout();
  }

  public setViewportSize(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.draw();
  }

  public show(): void {
    this.visible = true;
  }

  public hide(): void {
    this.visible = false;
  }

  private readonly handleOverlayTap = (event: FederatedPointerEvent): void => {
    event.stopPropagation();
    this.onDismiss?.();
  };

  private draw(): void {
    this.overlay.clear();
    this.overlay.rect(0, 0, this.viewportWidth, this.viewportHeight).fill({
      color: UI_COLORS.black,
      alpha: 0.68
    });
    this.panel.position.set(
      Math.round((this.viewportWidth - this.modalWidth) / 2),
      Math.round((this.viewportHeight - this.modalHeight) / 2)
    );
    this.layout();
  }

  private layout(): void {
    this.messageText.style.wordWrapWidth = this.modalWidth - 72;
    this.messageText.position.set(8, 0);
    this.actionLayer.position.set(8, this.modalHeight - 156);

    const gap = 16;
    const totalWidth =
      this.actionButtons.reduce((width, button) => width + button.width, 0) +
      Math.max(this.actionButtons.length - 1, 0) * gap;
    let cursorX = Math.max(0, this.modalWidth - 72 - totalWidth);

    for (const button of this.actionButtons) {
      button.position.set(cursorX, 0);
      cursorX += button.width + gap;
    }
  }
}
